import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { API_BASE_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import { startGoogleSignIn } from '../utils/googleAuth';

// Helper function to manually validate JWT token format and expiration
const validateToken = (token) => {
    try {
        // Check if token has three parts (header.payload.signature)
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.error('Invalid token format (not three parts)');
            return false;
        }
        
        // Decode the payload
        const payload = JSON.parse(atob(parts[1]));
        
        // Check for required fields
        if (!payload.id || !payload.exp) {
            console.error('Token missing required fields');
            return false;
        }
        
        // Check expiration
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp < now) {
            console.error('Token is expired');
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Token validation error:', error);
        return false;
    }
};

const Login = () => {
    const [formData, setFormData] = useState({
        usernameOrEmail: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Add this line
    // Email verification is now optional, so we don't need these states
    const cookies = new Cookies();
    const navigate = useNavigate();
    
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true); // Set loading to true

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                usernameOrEmail: formData.usernameOrEmail,
                password: formData.password
            });
            
            // Validate the received token before storing it
            const token = response.data.token;
            if (!token) {
                throw new Error('No token received from server');
            }
            
            // Validate token format and expiration
            if (!validateToken(token)) {
                console.error('Received invalid token from server');
                throw new Error('Authentication error: Invalid token received');
            }
            
            // Get the current domain for proper cookie setting
            const hostname = window.location.hostname;
            const isProduction = hostname !== 'localhost' && hostname !== '127.0.0.1';
            
            cookies.set('access_token', token, {
                path: '/',
                maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
                sameSite: 'lax',
                secure: isProduction, // Use secure cookies in production
                // Don't set domain for localhost, but allow it for production domains
                ...(isProduction && hostname.includes('.') && { domain: hostname.startsWith('www.') ? hostname.substring(4) : hostname })
            });
            
            // Also store token in localStorage as backup for environments where cookies might fail
            window.localStorage.setItem('auth_token', token);
            window.localStorage.setItem('userID', response.data.userID);
            window.localStorage.setItem('username', response.data.username || formData.usernameOrEmail);
            window.localStorage.setItem('isPowerUser', response.data.isPowerUser ? 'true' : 'false');
            window.localStorage.setItem('preferredRegion', response.data.preferredRegion || '');
            window.localStorage.setItem('vitalityFirst', response.data.vitalityFirst ? 'true' : 'false');
            
            // Force cookie setting with multiple attempts
            let attempts = 0;
            const maxAttempts = 3;
            
            while (attempts < maxAttempts) {
                const storedCookieToken = cookies.get('access_token');
                if (storedCookieToken) {
                    break;
                }
                
                // Try setting the cookie again
                const hostname = window.location.hostname;
                const isProduction = hostname !== 'localhost' && hostname !== '127.0.0.1';
                
                cookies.set('access_token', token, {
                    path: '/',
                    maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
                    sameSite: 'lax',
                    secure: isProduction,
                    ...(isProduction && hostname.includes('.') && { domain: hostname.startsWith('www.') ? hostname.substring(4) : hostname })
                });
                
                attempts++;
                
                // Small delay between attempts
                if (attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            
            // Double check that token was stored successfully
            const storedCookieToken = cookies.get('access_token');
            const storedLocalToken = localStorage.getItem('auth_token');
            
            console.log('Login successful, token set:', {
                receivedToken: !!token,
                inCookie: !!storedCookieToken,
                inLocalStorage: !!storedLocalToken,
                hostname,
                isProduction,
                attempts
            });
            
            // Configure axios default headers for subsequent requests
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            
            // Dispatch auth success event that auth.js is listening for
            window.dispatchEvent(new CustomEvent('auth-success', {
                detail: { 
                    success: true,
                    userId: response.data.userID,
                    token: token
                }
            }));

            // Dispatch additional event for components to refresh their auth state
            window.dispatchEvent(new CustomEvent('auth-token-updated', {
                detail: { 
                    token: token,
                    userId: response.data.userID
                }
            }));
            
            // Get redirect path from session storage or URL params
            const urlParams = new URLSearchParams(window.location.search);
            const urlRedirect = urlParams.get('redirect');
            const sessionRedirect = sessionStorage.getItem('redirectAfterLogin');
            const redirectPath = urlRedirect || sessionRedirect || '/searchrecipes';
            
            // Clear stored redirect path
            if (sessionRedirect) {
                sessionStorage.removeItem('redirectAfterLogin');
            }
            
            // Add a small delay to ensure cookies are set before navigation
            setTimeout(() => {
                // Double check token again before navigation
                const finalCookieToken = cookies.get('access_token');
                const finalLocalToken = localStorage.getItem('auth_token');
                
                if (!finalCookieToken && !finalLocalToken) {
                    console.error('Token storage failed after delay');
                    setError('Login failed: Unable to store authentication token. Please try again or enable cookies.');
                    return;
                }
                
                console.log('Navigation after login:', {
                    redirectPath,
                    finalCookieToken: !!finalCookieToken,
                    finalLocalToken: !!finalLocalToken
                });
                
                // Navigate to the redirect path
                navigate(redirectPath, { replace: true });
            }, 300); // Slightly longer delay to ensure all processing is complete
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false); // Set loading to false
        }
    };

    const handleGoogleLogin = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirect = urlParams.get('redirect');
        startGoogleSignIn(redirect);
    };

    // Email verification is now optional, so we don't need the verification UI

    return (
        <div className="auth-form">
            <h2>Login</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="usernameOrEmail">Username or Email</label>
                    <input
                        type="text"
                        id="usernameOrEmail"
                        name="usernameOrEmail"
                        value={formData.usernameOrEmail}
                        onChange={handleChange}
                        required
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="current-password"
                    />
                </div>
                <div className="forgot-password-link">
                    <Link to="/auth/forgot-password">Forgot Password?</Link>
                </div>
                <button type="submit" disabled={isLoading}>
                        {isLoading ? (
                             <div className="flex items-center justify-center">
                                <LoadingSpinner size="sm" color="primary" text="" />
                                <span className="ml-2">Logging In...</span>
                            </div>
                        ) : 'Login'}
                    </button>

                    {/* Google Sign-in Button */}
                    <div className="google-signin-container">
                        <p className="or-divider"><span>OR</span></p>
                        <button 
                            type="button" 
                            className="google-signin-btn" 
                            onClick={handleGoogleLogin}
                            disabled={isLoading}
                        >
                            <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" />
                            Sign in with Google
                        </button>
                    </div>
            </form>
            <p className="auth-switch">
                Don't have an account? <Link to="/auth/register">Register here</Link>
            </p>
        </div>
    );
};

export default Login; 